import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CANDIDATE_STAGE_ORDER,
  CANDIDATE_STAGE_LABELS,
  CANDIDATE_STAGES,
  TERMINAL_STAGES,
  BG_CHECK_STATUS_LABELS,
  NOTIF_CONFIG_ROLES,
  isValidStageTransition,
} from '@onboarding-dashboard/shared';
import type {
  CandidateWithRole,
  CandidateStage,
  OpenRoleDetail,
  PipelineResponse,
  HireType,
} from '@onboarding-dashboard/shared';
import { candidateService } from '../../services/candidate.js';
import { rolesService } from '../../services/roles.js';
import { useAuth } from '../../contexts/AuthContext.js';
import pipelineTemplate from './pipeline.html?raw';
import './pipeline.css';

const STAGE_DOT_CLASS: Record<string, string> = {
  application: 'stage-dot--application',
  interview: 'stage-dot--interview',
  offer: 'stage-dot--offer',
  onboarding_docs: 'stage-dot--onboarding_docs',
  complete: 'stage-dot--complete',
};

export function PipelinePage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManageCandidates = hasRole('admin') || hasRole('hr') || hasRole('manager');

  const [pipelineData, setPipelineData] = useState<PipelineResponse | null>(null);
  const [openRoles, setOpenRoles] = useState<OpenRoleDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [showClosedRoles, setShowClosedRoles] = useState(false);

  // ─── Helpers ───────────────────────────────────────────

  const showMessage = useCallback((type: 'error' | 'success', message: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLDivElement>(`#pipeline-${type}`);
    const msgEl = container.querySelector<HTMLSpanElement>(`#pipeline-${type}-message`);
    if (el && msgEl) {
      msgEl.textContent = message;
      el.style.display = 'block';
      if (type === 'success')
        setTimeout(() => {
          el.style.display = 'none';
        }, 4000);
    }
    const other = type === 'error' ? 'success' : 'error';
    const otherEl = container.querySelector<HTMLDivElement>(`#pipeline-${other}`);
    if (otherEl) otherEl.style.display = 'none';
  }, []);

  const showDragToast = useCallback(
    (type: 'success' | 'error' | 'warning', message: string, duration = 5000) => {
      const container = containerRef.current;
      if (!container) return;
      const toast = container.querySelector<HTMLDivElement>('#drag-toast');
      const msgEl = container.querySelector<HTMLSpanElement>('#drag-toast-message');
      if (!toast || !msgEl) return;
      toast.className = type === 'warning' ? 'pipeline-warning' : `pipeline-${type}`;
      msgEl.textContent = message;
      toast.style.display = '';
      if (duration > 0) {
        setTimeout(() => {
          toast.style.display = 'none';
        }, duration);
      }
    },
    []
  );

  // ─── Data Loading ──────────────────────────────────────

  const loadPipeline = useCallback(async () => {
    setIsLoading(true);
    const result = await candidateService.pipeline();
    if (result.success && result.data) {
      setPipelineData(result.data);
    } else {
      showMessage('error', result.error?.message ?? 'Failed to load pipeline');
    }
    setIsLoading(false);
  }, [showMessage]);

  const loadOpenRoles = useCallback(async () => {
    const result = await rolesService.list({ status: 'open' });
    if (result.success && result.data) {
      setOpenRoles(result.data.items);
    }
  }, []);

  useEffect(() => {
    void loadPipeline();
    void loadOpenRoles();
  }, [loadPipeline, loadOpenRoles]);

  // ─── Render Kanban Board ───────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const loading = container.querySelector<HTMLDivElement>('#pipeline-loading');
    const board = container.querySelector<HTMLDivElement>('#kanban-board');

    if (isLoading) {
      if (loading) loading.style.display = '';
      if (board) board.style.display = 'none';
      return;
    }

    if (loading) loading.style.display = 'none';
    if (board) board.style.display = '';

    if (!pipelineData || !board) return;

    // Build stage lookup, applying filters
    const stageMap = new Map<string, CandidateWithRole[]>();
    for (const s of pipelineData.stages) {
      let filtered = s.candidates;
      // Hide candidates from non-open roles unless toggle is on
      if (!showClosedRoles) {
        filtered = filtered.filter((c) => c.openRole.status === 'open');
      }
      if (roleFilter) {
        filtered = filtered.filter((c) => c.openRole.id === roleFilter);
      }
      stageMap.set(s.stage, filtered);
    }

    board.innerHTML = CANDIDATE_STAGE_ORDER.map((stage) => {
      const candidates = stageMap.get(stage) ?? [];
      const label = CANDIDATE_STAGE_LABELS[stage] ?? stage;
      const dotClass = STAGE_DOT_CLASS[stage] ?? '';

      const cardsHtml =
        candidates.length > 0
          ? candidates
              .map((c) => {
                let checklistHtml = '';
                if (stage === 'offer' && pipelineData.offerChecklist) {
                  const cl = pipelineData.offerChecklist[c.id];
                  if (cl) {
                    const agrDone = cl.agreementsSigned;
                    const bgStatus = cl.bgCheckStatus;
                    const bgDone = bgStatus === 'passed';
                    const bgFailed = bgStatus === 'failed';
                    const bgLabel = bgStatus
                      ? (BG_CHECK_STATUS_LABELS[bgStatus] ?? bgStatus)
                      : 'Not started';
                    checklistHtml = `
                      <div class="candidate-card-checklist">
                        <div class="checklist-item ${agrDone ? 'checklist-item--done' : ''}">
                          <span class="checklist-icon">${agrDone ? '&#x2611;' : '&#x2610;'}</span>
                          <span class="checklist-label">Agreements signed</span>
                        </div>
                        <div class="checklist-item ${bgDone ? 'checklist-item--done' : ''} ${bgFailed ? 'checklist-item--failed' : ''}">
                          <span class="checklist-icon">${bgDone ? '&#x2611;' : '&#x2610;'}</span>
                          <span class="checklist-label">Background check: ${escapeHtml(bgLabel)}</span>
                        </div>
                      </div>
                    `;
                  }
                }
                return `
          <div class="candidate-card" data-candidate-id="${c.id}">
            <div class="candidate-card-name">${escapeHtml(c.firstName)} ${escapeHtml(c.lastName)}</div>
            <div class="candidate-card-role">${escapeHtml(c.openRole.title)} · ${escapeHtml(c.openRole.department)}</div>
            <div class="candidate-card-email">${escapeHtml(c.email)}</div>
            ${checklistHtml}
          </div>
        `;
              })
              .join('')
          : '<div class="kanban-column-empty">No candidates</div>';

      return `
        <div class="kanban-column" data-stage="${stage}">
          <div class="kanban-column-header">
            <div class="kanban-column-title">
              <span class="kanban-stage-dot ${dotClass}"></span>
              ${escapeHtml(label)}
            </div>
            <span class="kanban-column-count">${candidates.length}</span>
          </div>
          <div class="kanban-column-body">
            ${cardsHtml}
          </div>
        </div>
      `;
    }).join('');
  }, [pipelineData, isLoading, roleFilter, showClosedRoles]);

  // ─── Drag and Drop ────────────────────────────────────

  const handleDrop = useCallback(
    async (candidateId: string, fromStage: string, toStage: string) => {
      const isInterview = toStage === CANDIDATE_STAGES.INTERVIEW;
      const result = await candidateService.transitionStage(candidateId, {
        toStage: toStage as CandidateStage,
        skipNotifications: isInterview || undefined,
      });

      if (!result.success) {
        showDragToast('error', result.error?.message ?? 'Failed to move candidate');
        // Re-render from existing state to revert the optimistic DOM move
        setPipelineData((prev) => (prev ? { ...prev } : null));
        return;
      }

      const candidateName = result.data
        ? `${result.data.firstName} ${result.data.lastName}`
        : 'Candidate';
      const stageLabel = CANDIDATE_STAGE_LABELS[toStage] ?? toStage;

      if (toStage === CANDIDATE_STAGES.INTERVIEW) {
        showDragToast('success', `Sending interview scheduling email to ${candidateName}…`, 0);

        const interviewResult = await candidateService.createInterview({ candidateId });

        if (interviewResult.success) {
          showDragToast('success', `Interview scheduling email sent to ${candidateName}`, 6000);
        } else {
          // Revert to previous stage since email failed
          const revertResult = await candidateService.transitionStage(candidateId, {
            toStage: fromStage as CandidateStage,
            rollback: true,
          });
          if (revertResult.success) {
            showDragToast(
              'error',
              `Interview email failed: ${interviewResult.error?.message ?? 'Unknown error'}. ${candidateName} moved back to ${CANDIDATE_STAGE_LABELS[fromStage] ?? fromStage}.`,
              8000
            );
          } else {
            showDragToast(
              'error',
              `Interview email failed: ${interviewResult.error?.message ?? 'Unknown error'}. Could not move ${candidateName} back automatically — please move them manually from the candidate detail page.`,
              10000
            );
          }
        }
      } else {
        showDragToast('success', `${candidateName} moved to ${stageLabel}`);
      }

      void loadPipeline();
    },
    [showDragToast, loadPipeline]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isLoading || !pipelineData) return;

    const board = container.querySelector<HTMLDivElement>('#kanban-board');
    if (!board) return;

    const terminalSet = new Set<string>(TERMINAL_STAGES);

    // Make non-terminal cards draggable
    const cards = board.querySelectorAll<HTMLElement>('.candidate-card');
    cards.forEach((card) => {
      const column = card.closest<HTMLElement>('.kanban-column');
      const stage = column?.dataset.stage;
      if (stage && terminalSet.has(stage)) return;
      card.setAttribute('draggable', 'true');
    });

    let draggedCard: HTMLElement | null = null;
    let draggedCandidateId: string | null = null;
    let draggedFromStage: string | null = null;

    const handleDragStart = (e: DragEvent): void => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.candidate-card');
      if (!card || card.getAttribute('draggable') !== 'true') return;

      draggedCard = card;
      draggedCandidateId = card.dataset.candidateId ?? null;
      const column = card.closest<HTMLElement>('.kanban-column');
      draggedFromStage = column?.dataset.stage ?? null;

      if (!draggedCandidateId || !draggedFromStage) return;

      if (e.dataTransfer) {
        e.dataTransfer.setData('text/plain', draggedCandidateId);
        e.dataTransfer.effectAllowed = 'move';
      }

      isDraggingRef.current = true;

      requestAnimationFrame(() => {
        card.classList.add('candidate-card--dragging');
      });

      // Highlight valid/invalid columns
      const columns = board.querySelectorAll<HTMLElement>('.kanban-column');
      columns.forEach((col) => {
        const colStage = col.dataset.stage;
        if (!colStage || !draggedFromStage) return;
        if (colStage === draggedFromStage) return;
        if (isValidStageTransition(draggedFromStage, colStage)) {
          col.classList.add('kanban-column--drop-valid');
        } else {
          col.classList.add('kanban-column--drop-invalid');
        }
      });
    };

    const handleDragEnd = (): void => {
      if (draggedCard) {
        draggedCard.classList.remove('candidate-card--dragging');
      }
      const columns = board.querySelectorAll<HTMLElement>('.kanban-column');
      columns.forEach((col) => {
        col.classList.remove(
          'kanban-column--drop-valid',
          'kanban-column--drop-invalid',
          'kanban-column--drop-hover'
        );
      });
      draggedCard = null;
      draggedCandidateId = null;
      draggedFromStage = null;
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 0);
    };

    const handleDragOver = (e: DragEvent): void => {
      const column = (e.target as HTMLElement).closest<HTMLElement>('.kanban-column');
      if (!column || !draggedFromStage) return;
      const colStage = column.dataset.stage;
      if (!colStage) return;

      if (isValidStageTransition(draggedFromStage, colStage)) {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }
      }
    };

    const handleDragEnter = (e: DragEvent): void => {
      const column = (e.target as HTMLElement).closest<HTMLElement>('.kanban-column');
      if (!column || !draggedFromStage) return;
      const colStage = column.dataset.stage;
      if (!colStage) return;

      if (isValidStageTransition(draggedFromStage, colStage)) {
        column.classList.add('kanban-column--drop-hover');
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      const column = (e.target as HTMLElement).closest<HTMLElement>('.kanban-column');
      if (!column) return;
      const related = e.relatedTarget as HTMLElement | null;
      if (related && column.contains(related)) return;
      column.classList.remove('kanban-column--drop-hover');
    };

    const handleDropEvent = (e: DragEvent): void => {
      e.preventDefault();
      const column = (e.target as HTMLElement).closest<HTMLElement>('.kanban-column');
      if (!column || !draggedCandidateId || !draggedFromStage) return;

      const toStage = column.dataset.stage;
      if (!toStage) return;
      if (!isValidStageTransition(draggedFromStage, toStage)) return;

      // Optimistically move the card into the target column so there's no visual snap-back
      if (draggedCard) {
        const targetBody = column.querySelector('.kanban-column-body');
        const sourceColumn = board.querySelector<HTMLElement>(
          `.kanban-column[data-stage="${draggedFromStage}"]`
        );
        if (targetBody) {
          // Remove "No candidates" placeholder in target
          const emptyEl = targetBody.querySelector('.kanban-column-empty');
          if (emptyEl) emptyEl.remove();

          targetBody.appendChild(draggedCard);

          // Update counts
          const sourceCount = sourceColumn?.querySelector('.kanban-column-count');
          const targetCount = column.querySelector('.kanban-column-count');
          if (sourceCount) {
            sourceCount.textContent = String(
              Math.max(0, parseInt(sourceCount.textContent ?? '0', 10) - 1)
            );
          }
          if (targetCount) {
            targetCount.textContent = String(parseInt(targetCount.textContent ?? '0', 10) + 1);
          }

          // Add "No candidates" to source if now empty
          const sourceBody = sourceColumn?.querySelector('.kanban-column-body');
          if (sourceBody && sourceBody.querySelectorAll('.candidate-card').length === 0) {
            sourceBody.innerHTML = '<div class="kanban-column-empty">No candidates</div>';
          }
        }
      }

      void handleDrop(draggedCandidateId, draggedFromStage, toStage);
    };

    board.addEventListener('dragstart', handleDragStart);
    board.addEventListener('dragend', handleDragEnd);
    board.addEventListener('dragover', handleDragOver);
    board.addEventListener('dragenter', handleDragEnter);
    board.addEventListener('dragleave', handleDragLeave);
    board.addEventListener('drop', handleDropEvent);

    return () => {
      board.removeEventListener('dragstart', handleDragStart);
      board.removeEventListener('dragend', handleDragEnd);
      board.removeEventListener('dragover', handleDragOver);
      board.removeEventListener('dragenter', handleDragEnter);
      board.removeEventListener('dragleave', handleDragLeave);
      board.removeEventListener('drop', handleDropEvent);
    };
  }, [pipelineData, isLoading, handleDrop]);

  // ─── Populate modal role dropdown (open roles only) ───

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const select = container.querySelector<HTMLSelectElement>('#candidate-role');
    if (!select) return;

    const placeholder = select.options[0];
    select.innerHTML = '';
    if (placeholder) select.appendChild(placeholder);

    for (const role of openRoles) {
      const option = document.createElement('option');
      option.value = role.id;
      option.textContent = `${role.title} — ${role.department}`;
      select.appendChild(option);
    }

    // Pre-fill hire type when a role is selected
    const hireTypeSelect = container.querySelector<HTMLSelectElement>('#candidate-hire-type');
    const handleRoleChange = () => {
      const selectedRole = openRoles.find((r) => r.id === select.value);
      if (selectedRole && hireTypeSelect) {
        hireTypeSelect.value = selectedRole.hireType ?? 'regular_hire';
      }
    };
    select.addEventListener('change', handleRoleChange);
    return () => select.removeEventListener('change', handleRoleChange);
  }, [openRoles]);

  // ─── Populate filter dropdown from all open roles ───

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const select = container.querySelector<HTMLSelectElement>('#filter-role');
    if (!select) return;

    const currentValue = select.value;
    const placeholder = select.options[0];
    select.innerHTML = '';
    if (placeholder) select.appendChild(placeholder);

    for (const role of openRoles) {
      const option = document.createElement('option');
      option.value = role.id;
      option.textContent = `${role.title} — ${role.department}`;
      select.appendChild(option);
    }

    // Preserve current selection if it's still valid, otherwise reset
    const validIds = new Set(openRoles.map((r) => r.id));
    if (currentValue && validIds.has(currentValue)) {
      select.value = currentValue;
    } else if (currentValue) {
      setRoleFilter('');
    }
  }, [openRoles]);

  // ─── Form Submit ───────────────────────────────────────

  const handleFormSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const submitBtn = container.querySelector<HTMLButtonElement>('#btn-submit-candidate');
      if (submitBtn?.disabled) return;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';
      }

      const formError = container.querySelector<HTMLDivElement>('#form-error');
      if (formError) formError.style.display = 'none';

      const firstName = container
        .querySelector<HTMLInputElement>('#candidate-first-name')!
        .value.trim();
      const lastName = container
        .querySelector<HTMLInputElement>('#candidate-last-name')!
        .value.trim();
      const email = container.querySelector<HTMLInputElement>('#candidate-email')!.value.trim();
      const phone = container.querySelector<HTMLInputElement>('#candidate-phone')!.value.trim();
      const openRoleId = container.querySelector<HTMLSelectElement>('#candidate-role')!.value;
      const hireType = container.querySelector<HTMLSelectElement>('#candidate-hire-type')!.value;

      const resumeInput = container.querySelector<HTMLInputElement>('#candidate-resume');
      const resumeFile = resumeInput?.files?.[0];

      const result = await candidateService.create({
        firstName,
        lastName,
        email,
        phone: phone || undefined,
        openRoleId,
        hireType: (hireType || 'regular_hire') as HireType,
      });

      if (result.success) {
        // Upload resume if a file was selected
        if (resumeFile && result.data) {
          const uploadResult = await candidateService.uploadResume(result.data.id, resumeFile);
          if (!uploadResult.success) {
            showMessage(
              'error',
              `Candidate added but resume upload failed: ${uploadResult.error?.message ?? 'Unknown error'}`
            );
          }
        }

        showMessage('success', `Candidate ${firstName} ${lastName} added`);
        const modal = container.querySelector<HTMLDivElement>('#candidate-modal');
        if (modal) modal.style.display = 'none';
        container.querySelector<HTMLFormElement>('#candidate-form')!.reset();
        void loadPipeline();
      } else {
        if (formError) {
          formError.textContent = result.error?.message ?? 'Failed to add candidate';
          formError.style.display = '';
        }
      }

      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add Candidate';
      }
    },
    [showMessage, loadPipeline]
  );

  // ─── Event Listeners ──────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Back link
    const backLink = container.querySelector<HTMLAnchorElement>('#back-link');
    const handleBack = (e: Event): void => {
      e.preventDefault();
      navigate('/');
    };
    backLink?.addEventListener('click', handleBack);

    // Role filter
    const filterRole = container.querySelector<HTMLSelectElement>('#filter-role');
    const handleFilterChange = (): void => {
      setRoleFilter(filterRole?.value ?? '');
    };
    filterRole?.addEventListener('change', handleFilterChange);

    // Closed roles toggle
    const closedToggle = container.querySelector<HTMLInputElement>('#filter-closed-roles');
    const handleClosedToggle = (): void => {
      setShowClosedRoles(closedToggle?.checked ?? false);
    };
    closedToggle?.addEventListener('change', handleClosedToggle);

    // Notification settings button (admin only)
    const notifSettingsBtn = container.querySelector<HTMLButtonElement>(
      '#btn-notification-settings'
    );
    if (notifSettingsBtn) notifSettingsBtn.style.display = NOTIF_CONFIG_ROLES.some(hasRole) ? '' : 'none';
    notifSettingsBtn?.addEventListener('click', () => navigate('/pipeline/notification-settings'));

    // Add candidate button (managers and above only)
    const addBtn = container.querySelector<HTMLButtonElement>('#btn-add-candidate');
    if (addBtn) addBtn.style.display = canManageCandidates ? '' : 'none';
    const handleAdd = (): void => {
      const modal = container.querySelector<HTMLDivElement>('#candidate-modal');
      if (modal) modal.style.display = '';
    };
    addBtn?.addEventListener('click', handleAdd);

    // Modal close/cancel
    const closeBtn = container.querySelector<HTMLButtonElement>('#btn-modal-close');
    const cancelBtn = container.querySelector<HTMLButtonElement>('#btn-modal-cancel');
    const closeModal = (): void => {
      const modal = container.querySelector<HTMLDivElement>('#candidate-modal');
      if (modal) modal.style.display = 'none';
    };
    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);

    // Form submit
    const form = container.querySelector<HTMLFormElement>('#candidate-form');
    form?.addEventListener('submit', handleFormSubmit);

    // Card clicks (navigate to candidate detail, suppressed during drag)
    const board = container.querySelector<HTMLDivElement>('#kanban-board');
    const handleCardClick = (e: Event): void => {
      if (isDraggingRef.current) return;
      const card = (e.target as HTMLElement).closest<HTMLElement>('.candidate-card');
      if (card) {
        const candidateId = card.dataset.candidateId;
        if (candidateId) navigate(`/candidates/${candidateId}`);
      }
    };
    board?.addEventListener('click', handleCardClick);

    return () => {
      backLink?.removeEventListener('click', handleBack);
      filterRole?.removeEventListener('change', handleFilterChange);
      closedToggle?.removeEventListener('change', handleClosedToggle);
      addBtn?.removeEventListener('click', handleAdd);
      closeBtn?.removeEventListener('click', closeModal);
      cancelBtn?.removeEventListener('click', closeModal);
      form?.removeEventListener('submit', handleFormSubmit);
      board?.removeEventListener('click', handleCardClick);
    };
  }, [navigate, handleFormSubmit, canManageCandidates]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: pipelineTemplate }} />;
}

// ─── Utility ──────────────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
